// Strona startowa (/home) — czysta logika: który widok (wariant) i z czego liczyć
// kafle kadry. Bez Reacta, żeby dało się ją przetestować na samych danych.
//
// Dwie warstwy, celowo rozdzielone:
//   1. CO wolno pokazać — wyłącznie uprawnienia sekcji (me.access, ta sama macierz,
//      którą pilnuje backend). Widok „Sztab" = sekcja „Raporty meczowe → Mecze"
//      (/api/home/team ma tę samą regułę), „Dyrektor sportowy" = daty kontraktów
//      (`squad.dlugosc` / `squad.kontrakty` — te same, po których /api/squad/players
//      zeruje daty). „Scout" (tabela + terminarz) widzi każde konto.
//   2. KTÓRY układ na start — rola konta (scout / dyrektor sportowy / head of
//      recruitment), bo to on odpowiada na pytanie „co ta osoba chce zobaczyć rano".
//      Rola tylko WYBIERA spośród widoków dozwolonych w (1): nowa rola dodana przez
//      admina na /admin dostaje widok z samych uprawnień, bez zmiany w kodzie.

import { hasAccess, isAdmin, type Me } from './auth'
import { ageIn, legiaContract, type LegiaContract } from '../squad/model'
import type { SquadPlayer } from '../squad/types'
import type { HomeFixture } from './types'
import { DATE_LOCALE, LANG } from '../i18n/lang'

export type HomeView = 'sztab' | 'scout' | 'kadra'

// Kolejność przełącznika (jak w projekcie: Sztab / analityk · Scout · Dyrektor sportowy).
export const HOME_VIEWS: { key: HomeView; label: string }[] = [
  { key: 'sztab', label: 'Sztab / analityk' },
  { key: 'scout', label: 'Scout' },
  { key: 'kadra', label: 'Dyrektor sportowy' },
]

// Widok startowy wg roli (klucze ról z training.app_role). Brak roli na liście →
// pierwszy dozwolony wg FALLBACK_ORDER.
const VIEW_BY_ROLE: Record<string, HomeView> = {
  dyrektor_sportowy: 'kadra',
  head_of_recruitment: 'kadra',
  scout: 'scout',
}
const FALLBACK_ORDER: HomeView[] = ['sztab', 'kadra', 'scout']

export interface HomeAccess {
  /** Ostatni mecz z liczbami i link do raportu pomeczowego (`raporty.mecze`). */
  matchReports: boolean
  /** Link „Analiza przeciwnika" i karty drużyn z tabeli / terminarza (`analiza.druzyny`). */
  opponentAnalysis: boolean
  /** Kadra i kontrakty (daty) — PRAWDZIWY dostęp konta, nie `useCan`: API Squad
   *  plannera stoi za logowaniem niezależnie od flagi AUTH_REQUIRED. Wymaga i dat
   *  (`squad.dlugosc` / `squad.kontrakty`), i wejścia do planera — bez wejścia
   *  /api/squad/players odpowiada 403. */
  contracts: boolean
  /** Link „Planer kadry" / „Wszystkie kontrakty" — wejście do /squad-planner (dowolna zakładka). */
  squadPlanner: boolean
}

/** Uprawnienia do kontraktów z samego konta (jak squadPerms w squad/model.ts). */
export function contractAccess(me: Me | null): Pick<HomeAccess, 'contracts' | 'squadPlanner'> {
  const can = (s: string) => isAdmin(me) || hasAccess(me, s, 'view')
  const squadPlanner = can('squad.planner') || can('squad.wiek') || can('squad.kontrakty')
  return { contracts: squadPlanner && (can('squad.dlugosc') || can('squad.kontrakty')), squadPlanner }
}

export function availableViews(a: HomeAccess): HomeView[] {
  return HOME_VIEWS.map((v) => v.key).filter((k) =>
    k === 'sztab' ? a.matchReports : k === 'kadra' ? a.contracts : true)
}

/** Widok z adresu (?widok=), o ile dozwolony — inaczej wg roli, inaczej pierwszy wg FALLBACK_ORDER. */
export function pickView(requested: string | null, roles: string[], views: HomeView[]): HomeView {
  if (requested && views.includes(requested as HomeView)) return requested as HomeView
  for (const r of roles) {
    const v = VIEW_BY_ROLE[r]
    if (v && views.includes(v)) return v
  }
  return FALLBACK_ORDER.find((v) => views.includes(v)) ?? 'scout'
}

// --- Terminarz ------------------------------------------------------------------

/** Pełne dni do meczu (0 = dziś) — liczone po datach kalendarzowych, nie po godzinach. */
export function daysUntil(isoDate: string, today: Date): number {
  const [y, m, d] = isoDate.split('-').map(Number)
  const target = Date.UTC(y, m - 1, d)
  const base = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((target - base) / 86_400_000)
}

/** „dziś" / „jutro" / „za 3 dni". */
export function daysLabel(n: number): string {
  if (n <= 0) return 'dziś'
  if (n === 1) return 'jutro'
  return `za ${n} dni`
}

/** Dzień mikrocyklu względem najbliższego meczu: „MD" w dniu meczu, „MD-3" do 7 dni przed. */
export function matchDayLabel(daysToMatch: number | null): string | null {
  if (daysToMatch == null || daysToMatch < 0 || daysToMatch > 7) return null
  return daysToMatch === 0 ? 'MD' : `MD-${daysToMatch}`
}

const WEEKDAYS = ['Nd', 'Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob']

/** „Sob 17.10" — dzień meczu w terminarzu. */
export function fixtureDay(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const wd = WEEKDAYS[new Date(y, m - 1, d).getDay()]
  return `${wd} ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}`
}

/** „11. kolejka · 16–19.10" — nagłówek najbliższej kolejki. */
export function roundTitle(fixtures: Pick<HomeFixture, 'match_date' | 'match_week'>[]): string {
  if (!fixtures.length) return 'Najbliższa kolejka'
  const days = fixtures.map((f) => f.match_date).sort()
  const dm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`
  const range = days[0] === days.at(-1)
    ? dm(days[0])
    : days[0].slice(5, 7) === days.at(-1)!.slice(5, 7)
      ? `${days[0].slice(8, 10)}–${dm(days.at(-1)!)}`
      : `${dm(days[0])}–${dm(days.at(-1)!)}`
  const week = fixtures[0].match_week
  return `${week ? `${week}. kolejka` : 'Kolejka'} · ${range}`
}

/** [lewa, prawa] z (nasze, rywala) — panele meczu stawiają gospodarza po lewej. */
export function bySide<T>(weAreHome: boolean, ours: T, theirs: T): [T, T] {
  return weAreHome ? [ours, theirs] : [theirs, ours]
}

// Panel „Następny mecz" pisze ostatni wynik jak sztab: „1:1 z Cracovią" — klub
// w narzędniku, krótko (bez miasta, chyba że w lidze są dwa kluby o tej nazwie).
// Odmiana z ręki, bo reguła na końcówkach myli się na co drugim klubie (Raków →
// Rakowem, Pogoń → Pogonią, Miedź → Miedzią). Klucz = nazwa kanoniczna z
// backend/app/club_names.py (API już ją nakłada). Klub spoza słownika idzie bez
// przyimka („1:1 · Hutnik Kraków") — lepiej niż „z Hutnik Kraków".
const CLUB_INSTRUMENTAL: Record<string, string> = {
  'Arka Gdynia': 'Arką',
  'Bruk-Bet Termalica Nieciecza': 'Termaliką',
  'Chrobry Głogów': 'Chrobrym',
  'Cracovia': 'Cracovią',
  'GKS Katowice': 'GKS-em Katowice',
  'GKS Tychy': 'GKS-em Tychy',
  'Górnik Łęczna': 'Górnikiem Łęczna',
  'Górnik Zabrze': 'Górnikiem',
  'Jagiellonia Białystok': 'Jagiellonią',
  'Korona Kielce': 'Koroną',
  'Lech Poznań': 'Lechem',
  'Lechia Gdańsk': 'Lechią',
  'Legia Warszawa': 'Legią',
  'ŁKS Łódź': 'ŁKS-em',
  'Miedź Legnica': 'Miedzią',
  'Motor Lublin': 'Motorem',
  'Odra Opole': 'Odrą',
  'Piast Gliwice': 'Piastem',
  'Pogoń Grodzisk Mazowiecki': 'Pogonią Grodzisk',
  'Pogoń Siedlce': 'Pogonią Siedlce',
  'Pogoń Szczecin': 'Pogonią',
  'Polonia Bytom': 'Polonią Bytom',
  'Polonia Warszawa': 'Polonią Warszawa',
  'Puszcza Niepołomice': 'Puszczą',
  'Radomiak Radom': 'Radomiakiem',
  'Raków Częstochowa': 'Rakowem',
  'Ruch Chorzów': 'Ruchem',
  'Stal Mielec': 'Stalą Mielec',
  'Stal Rzeszów': 'Stalą Rzeszów',
  'Śląsk Wrocław': 'Śląskiem',
  'Warta Poznań': 'Wartą',
  'Widzew Łódź': 'Widzewem',
  'Wieczysta Kraków': 'Wieczystą',
  'Wisła Kraków': 'Wisłą Kraków',
  'Wisła Płock': 'Wisłą Płock',
  'Zagłębie Lubin': 'Zagłębiem',
  'Znicz Pruszków': 'Zniczem',
}

/** „z Cracovią", „ze Śląskiem", „ze Stalą Mielec"; klub spoza słownika → null. */
export function withClub(name: string): string | null {
  if (LANG === 'en') return `vs ${name}` // wersja angielska — bez odmiany nazwy
  const instr = CLUB_INSTRUMENTAL[name]
  if (!instr) return null
  // „ze" przed s / z / ś / ź / ż + spółgłoska („ze Stalą", „ze Zniczem"), inaczej „z".
  return `${/^[szśźż][^aąeęioóuy]/i.test(instr) ? 'ze' : 'z'} ${instr}`
}

type LastResultLike = { goals_for: number; goals_against: number; opponent_name: string }

/** Wynik z perspektywy drużyny („1:1") i rywal („z Cracovią" / „· Hutnik Kraków"). */
export function lastResultParts(r: LastResultLike): { score: string; vs: string } {
  return { score: `${r.goals_for}:${r.goals_against}`, vs: withClub(r.opponent_name) ?? `· ${r.opponent_name}` }
}

/** „1:1 z Cracovią". */
export function lastResultLabel(r: LastResultLike): string {
  const { score, vs } = lastResultParts(r)
  return `${score} ${vs}`
}

/** „Czwartek, 15 października 2026" — nagłówek strony. */
export function longDate(today: Date): string {
  const s = new Intl.DateTimeFormat(DATE_LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(today)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Imię do powitania — pierwszy człon nazwy konta. */
export const firstName = (displayName: string | null | undefined) => displayName?.trim().split(/\s+/)[0] ?? null

// --- Kadra i kontrakty (widok „Dyrektor sportowy") ------------------------------

/** Dokładny wiek w dniu `today` z daty urodzenia (rok bez daty → rocznikowo). */
export function ageAt(p: Pick<SquadPlayer, 'birth_date' | 'birth_year'>, today: Date): number | null {
  if (p.birth_date) {
    const [y, m, d] = p.birth_date.split('-').map(Number)
    const had = today.getMonth() + 1 > m || (today.getMonth() + 1 === m && today.getDate() >= d)
    return today.getFullYear() - y - (had ? 0 : 1)
  }
  return p.birth_year ? today.getFullYear() - p.birth_year : null
}

export const isoDay = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** Ten sam dzień za `months` miesięcy (31.08 + 6 → ostatni dzień lutego). */
export function addMonths(today: Date, months: number): Date {
  const d = new Date(today.getFullYear(), today.getMonth() + months, 1)
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
  return new Date(d.getFullYear(), d.getMonth(), Math.min(today.getDate(), last))
}

/** Kadra pierwszego zespołu = wszyscy poza wypożyczonymi Z Legii (ci grają gdzie indziej). */
export const firstTeam = (players: SquadPlayer[]) => players.filter((p) => p.status !== 'loan_out')

export interface SquadSummary {
  size: number
  avgAge: number | null
  foreigners: number
  value: number                      // suma wartości z Transfermarktu (€)
  mostValuable: SquadPlayer | null
  loansOut: number
  loansIn: number
}

export function squadSummary(players: SquadPlayer[], today: Date): SquadSummary {
  const team = firstTeam(players)
  const ages = team.map((p) => ageAt(p, today)).filter((a): a is number => a != null)
  const valued = team.filter((p) => p.tm?.market_value)
  return {
    size: team.length,
    avgAge: ages.length ? ages.reduce((s, a) => s + a, 0) / ages.length : null,
    foreigners: team.filter((p) => p.tm?.nationality && p.tm.nationality !== 'Poland').length,
    value: valued.reduce((s, p) => s + (p.tm?.market_value ?? 0), 0),
    mostValuable: valued.reduce<SquadPlayer | null>(
      (best, p) => (!best || (p.tm?.market_value ?? 0) > (best.tm?.market_value ?? 0) ? p : best), null),
    loansOut: players.filter((p) => p.status === 'loan_out').length,
    loansIn: players.filter((p) => p.status === 'loan_in').length,
  }
}

export interface ExpiringRow {
  player: SquadPlayer
  contract: LegiaContract
  until: string
}

/** Związek z Legią kończący się w ciągu `months` miesięcy (kontrakt; u wypożyczonego
 *  DO nas — koniec wypożyczenia), najbliższe pierwsze. Daty już minione też tu są —
 *  zawodnik, któremu kontrakt wygasł, a dalej jest w kadrze, to decyzja na wczoraj. */
export function expiringContracts(players: SquadPlayer[], today: Date, months = 12): ExpiringRow[] {
  const limit = isoDay(addMonths(today, months))
  const rows: ExpiringRow[] = []
  for (const player of players) {
    const contract = legiaContract(player)
    if (contract.until && contract.until <= limit) rows.push({ player, contract, until: contract.until })
  }
  return rows.sort((a, b) => a.until.localeCompare(b.until) || a.player.last_name.localeCompare(b.player.last_name, 'pl'))
}

export interface YearBucket { label: string; year: number; count: number; open: boolean }

/** Kontrakty wg roku końca: od bieżącego roku, ostatni kubełek zbiera „N+". */
export function contractsByYear(players: SquadPlayer[], today: Date, span = 5): YearBucket[] {
  const first = today.getFullYear()
  const buckets: YearBucket[] = Array.from({ length: span }, (_, i) => ({
    label: i === span - 1 ? `${first + i}+` : String(first + i), year: first + i, count: 0, open: i === span - 1,
  }))
  for (const p of players) {
    const until = legiaContract(p).until
    if (!until) continue
    const y = parseInt(until.slice(0, 4), 10)
    const i = Math.max(0, Math.min(span - 1, y - first))
    buckets[i].count += 1
  }
  return buckets
}

export interface AgeShare { label: string; minutes: number; share: number }

// Wiek rocznikowy w sezonie (start sezonu − rocznik), jak na wykresie wiek × minuty
// i w Squad plannerze: „talent" = do 21 lat.
const AGE_GROUPS: { label: string; max: number }[] = [
  { label: 'do 21', max: 21 },
  { label: '22–25', max: 25 },
  { label: '26–29', max: 29 },
  { label: '30+', max: Infinity },
]

/** Udział w minutach oficjalnych sezonu wg grup wieku. Bez rocznika = poza wykresem. */
export function minutesByAge(players: SquadPlayer[], seasonStart: number): AgeShare[] {
  const sums = AGE_GROUPS.map(() => 0)
  for (const p of players) {
    const age = ageIn(p.birth_year, seasonStart)
    if (age == null || !p.minutes.minutes) continue
    sums[AGE_GROUPS.findIndex((g) => age <= g.max)] += p.minutes.minutes
  }
  const total = sums.reduce((s, v) => s + v, 0)
  return AGE_GROUPS.map((g, i) => ({ label: g.label, minutes: sums[i], share: total ? sums[i] / total : 0 }))
}

export interface PositionGroup { key: string; label: string; players: SquadPlayer[]; value: number }

// Grupy kadry z Kadry (TE-TA) — te same co w Squad plannerze (GROUP_SLOTS).
const POSITION_ORDER: { key: string; label: string }[] = [
  { key: 'BR', label: 'Bramkarze' },
  { key: 'ŚO', label: 'Środkowi obrońcy' },
  { key: '7/11', label: 'Wahadłowi' },
  { key: '8', label: 'Środkowi pomocnicy' },
  { key: '10', label: 'Dziesiątki' },
  { key: '9', label: 'Napastnicy' },
]

/** Kadra pierwszego zespołu wg grup pozycji, w grupie wg minut sezonu. Zawodnik bez
 *  grupy idzie do „Bez pozycji" (pokazujemy, żeby nikt nie zniknął z liczby). */
export function positionGroups(players: SquadPlayer[]): PositionGroup[] {
  const team = firstTeam(players)
  const known = new Set(POSITION_ORDER.map((g) => g.key))
  const groups = [...POSITION_ORDER, { key: '', label: 'Bez pozycji' }].map((g) => {
    const ps = team
      .filter((p) => (g.key ? p.position === g.key : !p.position || !known.has(p.position)))
      .sort((a, b) => b.minutes.minutes - a.minutes.minutes || a.last_name.localeCompare(b.last_name, 'pl'))
    return { ...g, players: ps, value: ps.reduce((s, p) => s + (p.tm?.market_value ?? 0), 0) }
  })
  return groups.filter((g) => g.key || g.players.length)
}

export interface LoanRow { player: SquadPlayer; club: string | null; until: string | null }

/** Wypożyczeni Z Legii (gdzie grają, do kiedy) i DO Legii (skąd, do kiedy). */
export function loans(players: SquadPlayer[]): { out: LoanRow[]; in: LoanRow[] } {
  const rows = (status: SquadPlayer['status']) => players
    .filter((p) => p.status === status)
    .map((player) => {
      const c = legiaContract(player)
      return { player, club: c.loanClub, until: c.loanUntil }
    })
    .sort((a, b) => (a.until ?? '9999').localeCompare(b.until ?? '9999'))
  return { out: rows('loan_out'), in: rows('loan_in') }
}
